// EXPORTS: IRecoveryAction, IRecoveryPlan, INJURY_ACTION_POOL, INJURY_PART_MAP, SLEEP_RELAX_ACTIONS, DAILY_RELAX_ACTIONS, generateInjuryRecoveryPlan, getTodayRecoveryPlan

export type InjuryPart = 'shoulder' | 'knee' | 'lower-back' | 'wrist' | 'ankle' | 'neck' | 'elbow' | 'hip';

export interface IRecoveryAction {
  id: string;
  name: string;
  /** 动作类别 */
  category: 'foam-roller' | 'stretch' | 'activation' | 'mobility';
  /** 建议描述，如"每侧 30 秒" */
  suggestion: string;
  /** 单组/单侧时长秒数（估算，用于总时长计算） */
  durationSec: number;
  /** 是否双侧（时长 × 2） */
  bilateral?: boolean;
}

// 各部位动作池（全部来自现有热身/拉伸/泡沫轴/激活类，不自编）
export const INJURY_ACTION_POOL: Record<InjuryPart, IRecoveryAction[]> = {
  shoulder: [
    { id: 'sh-fr-pec-minor', name: '泡沫轴·胸小肌放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'sh-mob-band-dis', name: '弹力带绕肩', category: 'mobility', suggestion: '15 次', durationSec: 40 },
    { id: 'sh-mob-half-rom', name: '肩关节半程活动', category: 'mobility', suggestion: '每侧 10 次', durationSec: 30, bilateral: true },
    { id: 'sh-act-y-raise', name: 'Y 字抬·下斜方激活', category: 'activation', suggestion: '12 次', durationSec: 30 },
    { id: 'sh-stretch-cross', name: '肩部交叉拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  ],
  knee: [
    { id: 'kn-fr-quad', name: '泡沫轴·大腿前侧放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'kn-fr-adductor', name: '泡沫轴·大腿内侧放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'kn-fr-tibialis', name: '泡沫轴·胫骨前肌放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'kn-act-foot-proprio', name: '脚本体感受激活', category: 'activation', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'kn-stretch-quad', name: '大腿前侧拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'kn-stretch-hamstring', name: '大腿后侧拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  ],
  'lower-back': [
    { id: 'lb-mob-thoracic-wall', name: '胸椎伸展·墙对抗', category: 'mobility', suggestion: '10 次', durationSec: 30 },
    { id: 'lb-mob-greatest-stretch', name: '最伟大伸展', category: 'mobility', suggestion: '每侧 5 次', durationSec: 30, bilateral: true },
    { id: 'lb-mob-inchworm', name: '毛毛虫伸展', category: 'mobility', suggestion: '8 次', durationSec: 30 },
    { id: 'lb-act-glute-bridge-light', name: '臀桥（轻量）', category: 'activation', suggestion: '15 次', durationSec: 30 },
    { id: 'lb-stretch-low-back', name: '腰背拉伸', category: 'stretch', suggestion: '30 秒', durationSec: 30 },
  ],
  wrist: [
    { id: 'wr-act-wrist-mob', name: '手腕激活', category: 'activation', suggestion: '15 次', durationSec: 30 },
    { id: 'wr-stretch-forearm', name: '前臂拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'wr-stretch-wrist-flex', name: '腕部屈伸拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'wr-act-scapular-retraction', name: '肩胛后缩', category: 'activation', suggestion: '15 次', durationSec: 30 },
  ],
  ankle: [
    { id: 'ak-act-foot-proprio', name: '脚本体感受激活', category: 'activation', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'ak-fr-tibialis', name: '泡沫轴·胫骨前肌放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'ak-stretch-calf', name: '小腿拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  ],
  neck: [
    { id: 'nk-act-rib-cage-expand', name: '胸廓扩张激活', category: 'activation', suggestion: '10 次深呼吸', durationSec: 40 },
    { id: 'nk-stretch-neck-shoulder', name: '颈肩拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'nk-act-scapular-retraction', name: '肩胛后缩', category: 'activation', suggestion: '15 次', durationSec: 30 },
  ],
  elbow: [
    { id: 'el-fr-biceps', name: '泡沫轴·二头肌放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'el-stretch-biceps', name: '二头肌拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'el-stretch-triceps', name: '三头肌拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'el-stretch-wrist-flexors', name: '腕伸屈肌拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  ],
  hip: [
    { id: 'hp-mob-dynamic-frog', name: '动态青蛙趴', category: 'mobility', suggestion: '10 次', durationSec: 40 },
    { id: 'hp-mob-9090-hip-rotate', name: '90/90 转髋', category: 'mobility', suggestion: '每侧 8 次', durationSec: 30, bilateral: true },
    { id: 'hp-fr-tfl', name: '泡沫轴·阔筋膜张肌放松', category: 'foam-roller', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'hp-stretch-glutes', name: '臀部拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
    { id: 'hp-act-glute-bridge', name: '臀桥', category: 'activation', suggestion: '15 次', durationSec: 30 },
  ],
};

const INJURY_LABELS: Record<InjuryPart, string> = {
  shoulder: '肩',
  knee: '膝',
  'lower-back': '腰',
  wrist: '腕',
  ankle: '踝',
  neck: '颈',
  elbow: '肘',
  hip: '髋',
};

/** 中文 → 英文 key 映射（问卷存中文，动作池用英文 key） */
export const INJURY_PART_MAP: Record<string, InjuryPart> = {
  '肩': 'shoulder',
  '膝': 'knee',
  '腰': 'lower-back',
  '腕': 'wrist',
  '踝': 'ankle',
  '颈': 'neck',
  '肘': 'elbow',
  '髋': 'hip',
};

/** 英文 key → 中文 label 反向映射（身体信号→profile 同步用） */
export const INJURY_PART_LABEL_REVERSE: Record<InjuryPart, string> = {
  shoulder: '肩',
  knee: '膝',
  'lower-back': '腰',
  wrist: '腕',
  ankle: '踝',
  neck: '颈',
  elbow: '肘',
  hip: '髋',
};

/** 伤病部位 → 需要规避的动作身体部位/子分类映射
 *  key: 伤病部位英文 key
 *  value: { bodyParts?: BodyPart[]; subCategories?: string[]; equipments?: string[] }
 *  训练计划生成时，命中任一维度的动作将被过滤或替换 */
export const INJURY_AVOID_MAP: Record<InjuryPart, {
  bodyParts?: string[];
  subCategories?: string[];
  keywords?: string[];
}> = {
  shoulder: {
    bodyParts: ['shoulders', 'chest'],
    subCategories: ['overhead-press', 'bench-press', 'shoulder-press', 'lateral-raise', 'front-raise', 'rear-delt-fly'],
    keywords: ['推举', '卧推', '飞鸟', '侧平举', '前平举', '过顶'],
  },
  knee: {
    bodyParts: ['legs'],
    subCategories: ['squat', 'lunge', 'jump', 'deadlift', 'leg-press', 'leg-extension'],
    keywords: ['深蹲', '箭步蹲', '跳跃', '硬拉', '腿举', '腿屈伸'],
  },
  'lower-back': {
    bodyParts: ['back', 'legs'],
    subCategories: ['deadlift', 'row', 'squat', 'good-morning', 'hyperextension'],
    keywords: ['硬拉', '划船', '深蹲', '挺身', '山羊挺身'],
  },
  wrist: {
    subCategories: ['push-up', 'bench-press', 'overhead-press', 'plank'],
    keywords: ['俯卧撑', '卧推', '推举', '平板支撑', '倒立'],
  },
  ankle: {
    bodyParts: ['legs'],
    subCategories: ['jump', 'lunge', 'calf-raise', 'running', 'box-jump'],
    keywords: ['跳跃', '箭步蹲', '提踵', '跑步', '跳箱'],
  },
  neck: {
    subCategories: ['neck'],
    keywords: ['颈', '颈桥', '颈屈伸'],
  },
  elbow: {
    bodyParts: ['arms', 'chest', 'shoulders'],
    subCategories: ['bicep-curl', 'tricep-extension', 'bench-press', 'push-up', 'overhead-press'],
    keywords: ['二头弯举', '三头屈伸', '卧推', '俯卧撑', '推举'],
  },
  hip: {
    bodyParts: ['legs', 'back'],
    subCategories: ['squat', 'deadlift', 'hip-thrust', 'good-morning', 'bulgarian-lunge'],
    keywords: ['深蹲', '硬拉', '臀推', '早安式', '保加利亚分腿蹲'],
  },
};
export const SLEEP_RELAX_ACTIONS: IRecoveryAction[] = [
  { id: 'slp-stretch-neck-shoulder', name: '颈肩舒缓拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  { id: 'slp-mob-thoracic', name: '胸椎旋转放松', category: 'mobility', suggestion: '每侧 8 次', durationSec: 30, bilateral: true },
  { id: 'slp-act-rib-cage', name: '腹式呼吸·胸廓扩张', category: 'activation', suggestion: '10 次深呼吸', durationSec: 60 },
];

/** 日常放松通用动作池（无伤病且睡眠正常时可选的轻量维护动作） */
export const DAILY_RELAX_ACTIONS: IRecoveryAction[] = [
  { id: 'daily-fr-back', name: '泡沫轴·背部放松', category: 'foam-roller', suggestion: '60 秒', durationSec: 60 },
  { id: 'daily-mob-cat-cow', name: '猫牛式·脊柱活动', category: 'mobility', suggestion: '10 次', durationSec: 40 },
  { id: 'daily-stretch-neck-shoulder', name: '颈肩拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  { id: 'daily-stretch-chest', name: '胸部开门拉伸', category: 'stretch', suggestion: '30 秒', durationSec: 30 },
  { id: 'daily-stretch-hamstring', name: '大腿后侧拉伸', category: 'stretch', suggestion: '每侧 30 秒', durationSec: 30, bilateral: true },
  { id: 'daily-act-deep-breath', name: '腹式深呼吸', category: 'activation', suggestion: '8 次呼吸', durationSec: 50 },
];

export interface IRecoveryPlan {
  /** 伤病部位列表 */
  injuryParts: InjuryPart[];
  /** 按天生成的 7 天轮换计划（每天一个完整小计划） */
  dailyPlans: {
    dayIndex: number;
    /** 当日主攻部位（或"综合恢复"） */
    focusPart: InjuryPart | 'mixed';
    title: string;
    actions: IRecoveryAction[];
    /** 总时长（分钟，向上取整） */
    totalMin: number;
  }[];
  /** 生成时间戳 */
  generatedAt: number;
}

/**
 * 根据伤病部位生成恢复计划。
 * 多部位：按天轮换不同部位（7 天循环）
 * 单部位：每天从动作池轮换组合（起始动作偏移）
 */
export function generateInjuryRecoveryPlan(
  injuryParts: InjuryPart[],
  sleepQuality: 'good' | 'normal' | 'poor' = 'normal',
  now: number = Date.now(),
): IRecoveryPlan {
  const validParts = injuryParts.filter(p => INJURY_ACTION_POOL[p]);

  const dailyPlans: IRecoveryPlan['dailyPlans'] = Array.from({ length: 7 }, (_, dayIdx) => {
    let focusPart: InjuryPart | 'mixed';
    let actions: IRecoveryAction[];

    if (validParts.length === 0) {
      // 无伤病：通用恢复（腰+肩组合）
      focusPart = 'mixed';
      actions = [
        INJURY_ACTION_POOL['lower-back'][4],
        INJURY_ACTION_POOL.shoulder[4],
        INJURY_ACTION_POOL.neck[1],
      ];
    } else if (validParts.length === 1) {
      // 单部位：每天从动作池轮换起始点
      const pool = INJURY_ACTION_POOL[validParts[0]];
      focusPart = validParts[0];
      const startIdx = dayIdx % pool.length;
      // 取 4 个动作：从起始位置开始环形取
      actions = Array.from({ length: Math.min(4, pool.length) }, (__, i) => pool[(startIdx + i) % pool.length]);
    } else {
      // 多部位：按天轮换主攻部位，每天从该部位取 3-4 个动作
      const partIdx = dayIdx % validParts.length;
      focusPart = validParts[partIdx];
      const pool = INJURY_ACTION_POOL[focusPart];
      const startIdx = Math.floor(dayIdx / validParts.length) % pool.length;
      const count = Math.min(4, pool.length);
      actions = Array.from({ length: count }, (__, i) => pool[(startIdx + i) % pool.length]);
    }

    const totalSec = actions.reduce((sum, a) => sum + a.durationSec * (a.bilateral ? 2 : 1), 0);
    const totalMin = Math.max(5, Math.ceil(totalSec / 60));

    const titleBase = validParts.length === 0
      ? '今日恢复·全身放松'
      : `今日恢复·${INJURY_LABELS[focusPart === 'mixed' ? validParts[0] : focusPart]}部恢复`;
    const title = sleepQuality === 'poor' ? `${titleBase}·助眠版` : titleBase;

    return {
      dayIndex: dayIdx,
      focusPart,
      title,
      actions: sleepQuality === 'poor' ? [...actions, ...SLEEP_RELAX_ACTIONS.slice(0, 2)] : actions,
      totalMin: sleepQuality === 'poor' ? totalMin + 2 : totalMin,
    };
  });

  return {
    injuryParts: validParts,
    dailyPlans,
    generatedAt: now,
  };
}

/**
 * 取今日恢复计划（基于生成时间起算，按天轮换）
 */
export function getTodayRecoveryPlan(plan: IRecoveryPlan) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const genDate = new Date(plan.generatedAt);
  genDate.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - genDate.getTime()) / (24 * 60 * 60 * 1000));
  const idx = ((diffDays % 7) + 7) % 7;
  return plan.dailyPlans[idx];
}
